import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { ColorType, Icon, Label, LabelType, Pill } from '..';
import { IconSize } from '../../../constant/IconSize.constant';
import { getCurrentTheme } from '../../../stores/selectors/theme.selector';

interface CheckBoxProps {
  name?: string;
  label?: string;
  selected?: boolean;
  indeterminate?: boolean;
  onChange?: Function;
  isDisabled?: boolean;
  singleSelection?: boolean;
  index?: number;
  style?: any;
  checkRef?: any;
  translationOn?: boolean;
  icon?: string;
  labelType?: LabelType;
  secondaryLabelType?: LabelType;
  labelColor?: ColorType;
  secondaryLabelColor?: ColorType;
  pillLabel?: string;
  pillIcon?: any;
  selectedLabelColor?: string;
  secondaryLabel?: string;
  preLabelIcon?: any;
  dataTestId?: string;
  title?: string;
  preLabelIconAlt?: string;
}

const CheckBox = ({
  name = '',
  onChange = () => {},
  label = '',
  selected = false,
  indeterminate = false,
  isDisabled = false,
  singleSelection = false,
  index = -1,
  style,
  checkRef,
  selectedLabelColor = '',
  translationOn = false,
  icon = '',
  labelType = LabelType.BODY3,
  labelColor = ColorType.BLACK,
  secondaryLabelColor = ColorType.BLACK,
  pillLabel,
  pillIcon,
  secondaryLabel = '',
  secondaryLabelType = LabelType.BODY3,
  preLabelIcon = '',
  dataTestId,
  title = '',
  preLabelIconAlt = '',
}: CheckBoxProps) => {
  const { t } = useTranslation();

  const uniqueId = `checkbox-${uuidv4()}`;

  const [checked, setChecked] = useState(selected);
  const theme = useSelector(getCurrentTheme);

  useEffect(() => {
    if (checked !== selected) {
      setChecked(selected);
    }
    // eslint-disable-next-line
  }, [selected]);

  const handleChange = (value: any) => {
    if (singleSelection) {
      setChecked(true);
    } else {
      setChecked(!value);
    }

    if (onChange) onChange(!value, index);
  };

  return (
    <div className='flex flex-col items-start w-full h-full gap-1'>
      <div className='flex flex-row items-center w-full h-full'>
        <div
          data-testid={dataTestId || label}
          className='flex flex-row gap-2 items-center grow h-full'
          onClick={(e: any) => {
            if (!isDisabled && e.type !== 'checkbox' && e.target.tagName !== 'LABEL') {
              handleChange(checked);
            }
          }}
          onKeyDown={(e: any) => {
            if (e.key === 'Enter' && !isDisabled) {
              handleChange(checked);
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <input
            className={`w-4 h-4 justify-center ${
              isDisabled ? 'text-grey3 border-grey0 bg-grey0' : 'text-brand2 '
            } ${singleSelection ? 'form-radio' : 'rounded-sm form-checkbox'}`}
            type='checkbox'
            checked={checked}
            disabled={isDisabled}
            ref={(el) => {
              if (el) el.indeterminate = indeterminate;
              if (checkRef) checkRef.current = el;
            }}
            name={name}
            value={label}
            onChange={() => {
              handleChange(checked);
            }}
            style={{ color: theme.navigationSelectedColor, ...style }}
            readOnly
            id={uniqueId}
            title={title}
            aria-label={title}
          />
          {!!preLabelIcon && (
            <Icon src={preLabelIcon} size={IconSize.SIZE_16x16} alt={preLabelIconAlt} />
          )}
          {label && (
            <Label
              labelHtmlFor={uniqueId}
              htmlFor={uniqueId}
              type={isDisabled ? LabelType.BODY3_DISABLED : labelType}
              text={translationOn ? t(label) : label}
              icon={icon}
              lazyIcon
              style={{
                display: 'flex',
                alignItems: 'center',
                textAlign: 'left',
                cursor: 'pointer',
              }}
              color={isDisabled ? ColorType.GREY3 : labelColor}
            />
          )}
        </div>
        {pillLabel && (
          <div className='pr-[8px] flex-none'>
            <Pill label={pillLabel} iconLeft={pillIcon} />
          </div>
        )}
      </div>
      {secondaryLabel && (
        <Label
          className='pl-6'
          type={isDisabled ? LabelType.BODY3_DISABLED : secondaryLabelType}
          text={translationOn ? t(secondaryLabel) : secondaryLabel}
          style={{ display: 'flex', alignItems: 'center' }}
          color={secondaryLabelColor}
        />
      )}
    </div>
  );
};

export default memo<CheckBoxProps>(CheckBox);
